/**
 * DataPagination — reusable pagination component with page numbers and ellipsis.
 *
 * Usage:
 *   <DataPagination
 *     page={listState.page}
 *     totalPages={data?.pages ?? 1}
 *     onPageChange={listState.setPage}
 *   />
 */
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'

export interface DataPaginationProps {
    /** Current page (1-based) */
    page: number
    /** Total number of pages */
    totalPages: number
    /** Callback when page changes */
    onPageChange: (page: number) => void
    /** Optional: show "Showing X–Y of Z" text */
    totalItems?: number
    perPage?: number
}

function buildPageNumbers(current: number, total: number): (number | 'ellipsis')[] {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1)
    }

    const pages: (number | 'ellipsis')[] = [1]

    if (current > 3) pages.push('ellipsis')

    for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
        pages.push(i)
    }

    if (current < total - 2) pages.push('ellipsis')

    pages.push(total)

    return pages
}

export function DataPagination({
    page,
    totalPages,
    onPageChange,
    totalItems,
    perPage,
}: DataPaginationProps) {
    if (totalPages <= 1) return null

    const pageNums = buildPageNumbers(page, totalPages)

    return (
        <div className="flex items-center justify-between pt-4 border-t">
            {/* Info text */}
            <div className="text-sm text-muted-foreground">
                {totalItems !== undefined && perPage ? (
                    <>
                        Showing {Math.min((page - 1) * perPage + 1, totalItems)}–{Math.min(page * perPage, totalItems)} of {totalItems}
                    </>
                ) : (
                    <>Page {page} of {totalPages}</>
                )}
            </div>

            {/* Page buttons */}
            <div className="flex items-center gap-1">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page <= 1}
                    className="h-8"
                >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                </Button>

                {pageNums.map((p, idx) =>
                    p === 'ellipsis' ? (
                        <span key={`e-${idx}`} className="px-2 text-muted-foreground select-none">…</span>
                    ) : (
                        <Button
                            key={p}
                            variant={p === page ? 'default' : 'outline'}
                            size="sm"
                            className="min-w-[2rem] h-8"
                            onClick={() => onPageChange(p)}
                        >
                            {p}
                        </Button>
                    )
                )}

                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page >= totalPages}
                    className="h-8"
                >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
            </div>
        </div>
    )
}
